import React, { memo } from "react";
import { Text, View } from "react-native";
import Svg, { Path } from "react-native-svg";
import { StyleSheet } from "@/utils/appStyleSheet";
import { FontFamilies } from "@/constants/Typography";
import { Shadows } from "@/constants/Shadows";
import { LineChart } from "react-native-gifted-charts";
import { UserFinanceDataResponse } from "@/types/financeData";

interface IncomeNetWorthCardProps {
  financeData: UserFinanceDataResponse | null;
}

// Helper to format currency from cents
const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;
};

// Arrow up icon component
const ArrowUpIcon = () => (
  <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
    <Path
      d="M8 3.33334V12.6667M8 3.33334L12 7.33334M8 3.33334L4 7.33334"
      stroke="#0F9129"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Arrow down icon component
const ArrowDownIcon = () => (
  <Svg width={16} height={16} viewBox="0 0 16 16" fill="none">
    <Path
      d="M8 12.6667V3.33334M8 12.6667L12 8.66667M8 12.6667L4 8.66667"
      stroke="#C12915"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// Chart data point type with optional styling
interface ChartDataPoint {
  value: number;
  hideDataPoint?: boolean;
  dataPointColor?: string;
  dataPointRadius?: number;
}

// Monthly aggregated data
interface MonthlyData {
  month: string; // "2025-01"
  balance: number; // End of month balance (last day of that month)
}

// Aggregate daily data into monthly data (last day's balance per month)
const aggregateToMonthly = (
  financeData: UserFinanceDataResponse | null
): MonthlyData[] | null => {
  const byDay = financeData?.analysis?.netWorth?.byDay;
  if (!byDay?.length) return null;

  const monthlyMap: Record<string, number> = {};

  // For each day, track the balance by month key (YYYY-MM)
  // The last entry for each month will be the end-of-month balance
  for (const day of byDay) {
    const monthKey = day.date.substring(0, 7); // "2025-01-15" -> "2025-01"
    monthlyMap[monthKey] = day.balance;
  }

  // Convert to sorted array
  return Object.entries(monthlyMap)
    .map(([month, balance]) => ({ month, balance }))
    .sort((a, b) => a.month.localeCompare(b.month));
};

// Generate mini chart data from monthly aggregated data
// Normalizes values to 0-100 range to prevent extreme spikes from breaking the chart
const generateMiniChartData = (
  financeData: UserFinanceDataResponse | null
): ChartDataPoint[] | null => {
  const monthlyData = aggregateToMonthly(financeData);
  if (!monthlyData?.length) return null;

  const values = monthlyData.map((m) => m.balance / 100);
  const minVal = Math.min(...values);
  const maxVal = Math.max(...values);
  const range = maxVal - minVal;

  return monthlyData.map((month, index) => {
    const isLast = index === monthlyData.length - 1;
    // Normalize to 0-100 range, or use 50 if all values are the same
    const normalized =
      range === 0 ? 50 : ((month.balance / 100 - minVal) / range) * 100;
    return {
      value: normalized,
      hideDataPoint: !isLast,
      dataPointColor: isLast ? "#0F9129" : undefined,
      dataPointRadius: isLast ? 5 : undefined,
    };
  });
};

// Get net worth value from the last day's balance
const getNetWorth = (financeData: UserFinanceDataResponse | null): number => {
  const byDay = financeData?.analysis?.netWorth?.byDay;
  if (!byDay?.length) return 0;
  return byDay[byDay.length - 1].balance;
};

// Calculate net worth trend (month-over-month)
const calculateNetWorthTrend = (
  financeData: UserFinanceDataResponse | null
): number => {
  const monthlyData = aggregateToMonthly(financeData);
  if (!monthlyData || monthlyData.length < 2) return 0;
  const currentMonth = monthlyData[monthlyData.length - 1].balance;
  const previousMonth = monthlyData[monthlyData.length - 2].balance;
  if (previousMonth === 0) return 0;
  const pct = ((currentMonth - previousMonth) / Math.abs(previousMonth)) * 100;
  // Use 1 decimal place for small changes so we don't round to 0%
  if (Math.abs(pct) < 1 && Math.abs(pct) > 0) {
    return parseFloat(pct.toFixed(1));
  }
  return Math.round(pct);
};

export default memo(function IncomeNetWorthCard({
  financeData,
}: IncomeNetWorthCardProps) {
  const netWorth = getNetWorth(financeData);
  const netWorthTrend = calculateNetWorthTrend(financeData);
  const chartData = generateMiniChartData(financeData);

  return (
    <View style={styles.card}>
      <View style={styles.content}>
        {/* Mini Chart */}
        {chartData && chartData.length > 0 && (
          <View style={styles.miniChartContainer}>
            <LineChart
              data={chartData}
              width={100}
              height={50}
              hideAxesAndRules
              hideYAxisText
              hideOrigin
              yAxisOffset={0}
              thickness={2}
              color="#0F9129"
              areaChart
              startFillColor="rgba(15, 145, 41, 0.2)"
              endFillColor="rgba(15, 145, 41, 0)"
              startOpacity={0.5}
              endOpacity={0}
              mostNegativeValue={-1}
              initialSpacing={5}
              endSpacing={10}
              spacing={(100 - 10) / (chartData.length - 1)}
              adjustToWidth
              disableScroll
            />
          </View>
        )}

        {/* Net Worth Info */}
        <View style={styles.netWorthInfo}>
          <Text style={styles.label}>Net worth</Text>
          <Text style={styles.value}>{formatCurrency(netWorth)}</Text>
          <View style={styles.trendRow}>
            {netWorthTrend >= 0 ? <ArrowUpIcon /> : <ArrowDownIcon />}
            <Text
              style={
                netWorthTrend >= 0
                  ? styles.trendTextPositive
                  : styles.trendTextNegative
              }
            >
              {netWorthTrend}%
            </Text>
          </View>
        </View>
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 20,
    paddingVertical: 24,
    ...Shadows.card,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 40,
  },
  miniChartContainer: {
    width: 120,
    height: 54,
  },
  netWorthInfo: {
    gap: 4,
  },
  label: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Medium,
    color: "#A3A3A3",
    lineHeight: 23,
  },
  value: {
    fontSize: 28,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#161D14",
    lineHeight: 34,
  },
  trendRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  trendTextPositive: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#0F9129",
    lineHeight: 16,
  },
  trendTextNegative: {
    fontSize: 16,
    fontFamily: FontFamilies.Inter.SemiBold,
    color: "#C12915",
    lineHeight: 16,
  },
});
